/**
 * dates.ts · the section 09 canonical date presentation (Member Changes A6/D10).
 *
 * One deterministic formatter pair for every display surface in section 09:
 *   fmtCdxDateTime -> 'YYYY-MM-DD, H:MM AM' for time-bearing values
 *   fmtCdxDate     -> 'YYYY-MM-DD' for date-only values
 *
 * The stored formats are irreducibly mixed (ISO-Z instants, zone-less
 * 'YYYY-MM-DD HH:MM[:SS]' wall clocks, date-only 'YYYY-MM-DD', US 'M/D/YYYY'),
 * so the formatters detect the shape: date-only strings NEVER pass through
 * new Date() (the UTC day-shift trap); zone-less wall clocks format from their
 * own digits; only true instants (T + Z/offset) convert through Date into the
 * browser's local time. Unknown shapes render raw (fail-soft, never blank).
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function fmt12h(h: number, m: number): string {
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad2(m)} ${h < 12 ? 'AM' : 'PM'}`;
}

/** 'YYYY-MM-DD, H:MM AM' for time-bearing values; date-only values stay 'YYYY-MM-DD'. */
export function fmtCdxDateTime(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '';
  if (DATE_ONLY.test(v)) return v;
  const us = US_DATE.exec(v);
  if (us) return `${us[3]}-${pad2(Number(us[1]))}-${pad2(Number(us[2]))}`;
  const w = WALL_CLOCK.exec(v);
  if (w) return `${w[1]}-${w[2]}-${w[3]}, ${fmt12h(Number(w[4]), Number(w[5]))}`;
  const t = new Date(v);
  if (!Number.isNaN(t.getTime())) {
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}, ${fmt12h(t.getHours(), t.getMinutes())}`;
  }
  return v;
}

/** 'YYYY-MM-DD' from any stored shape (time part dropped). */
export function fmtCdxDate(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '';
  if (DATE_ONLY.test(v)) return v;
  const us = US_DATE.exec(v);
  if (us) return `${us[3]}-${pad2(Number(us[1]))}-${pad2(Number(us[2]))}`;
  const w = WALL_CLOCK.exec(v);
  if (w) return `${w[1]}-${w[2]}-${w[3]}`;
  const t = new Date(v);
  if (!Number.isNaN(t.getTime())) {
    return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
  }
  return v;
}
